// A catalog colour's name (ops plan 2026-10-23-catalog-colour-names, B1066). Legacy stores an item's and a category's
// colour by name (`products.color`, `categories.color`); the catalog stores the register's hex for it in `color`, and the
// name beside it in `colorName`. Two names share a register hex (blue / light-blue, grey / gray), so the name is what
// tells them apart. Each surface maps the name to its own shade (the register theme's `primary-<name>`, the back office's
// `backofficePalette`).

/**
 * Every catalog colour name with the register hex the catalog stores for it, in the register palette's order (legacy
 * register `src/library/color.ts`, `@radish/ui-tokens` productPalette): legacy `App\Types\Color`'s 22 names plus the
 * palette's own (grey, orange-deep, cash-green, yellow). Where two names share a hex, the first is the one a hex alone reads as.
 */
export const CATALOG_COLOR_HEX = {
  blue: '#0579f7',
  'deep-purple': '#512da8',
  pink: '#e6457a',
  green: '#04c635',
  indigo: '#4f62cc',
  maroon: '#9a0a0a',
  red: '#f4261b',
  cyan: '#58bae8',
  gold: '#dcb332',
  orange: '#ff9500',
  'orange-deep': '#ff7031',
  teal: '#08bad0',
  brown: '#7e6250',
  purple: '#7A62E0',
  'dark-green': '#0A7602',
  grey: '#9da0a0',
  gray: '#9da0a0',
  'light-green': '#62ce96',
  'light-blue': '#0579f7',
  'dark-blue': '#005ca1',
  salmon: '#ef6e7e',
  'very-dark': '#1c232e',
  'gray-alternate': '#859da4',
  'lime-green': '#2dbf52',
  'cash-green': '#4caf50',
  yellow: '#F0D064',
} as const;

export type CatalogColorName = keyof typeof CATALOG_COLOR_HEX;

export const CATALOG_COLOR_NAMES = Object.keys(CATALOG_COLOR_HEX) as [CatalogColorName, ...CatalogColorName[]];

const isName = (name: string): name is CatalogColorName => Object.hasOwn(CATALOG_COLOR_HEX, name);

const sameHex = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

/** The register hex the catalog stores for a colour name, in lower case as the importer and the back office write it. */
export const catalogColorHex = (name: CatalogColorName): string => CATALOG_COLOR_HEX[name].toLowerCase();

/**
 * The name a stored catalog colour reads as: the stored name when it is the stored hex's (so light-blue stays light-blue),
 * else the first name with that hex (a hex-only write from an older writer beats a stale name), else null (a hex no
 * name has: drawn as stored). A colour with no hex has no name.
 */
export function catalogColorName(color: string | null | undefined, colorName: string | null | undefined): CatalogColorName | null {
  if (color === null || color === undefined) return null;
  if (colorName !== null && colorName !== undefined && isName(colorName) && sameHex(CATALOG_COLOR_HEX[colorName], color)) return colorName;
  return CATALOG_COLOR_NAMES.find((n) => sameHex(CATALOG_COLOR_HEX[n], color)) ?? null;
}

/** A catalog write's colour fields. `color` is a product's nullable hex or a category's hex. */
export interface CatalogColorChange {
  readonly color?: string | null | undefined;
  readonly colorName?: CatalogColorName | null | undefined;
}

/**
 * The colour fields a catalog write stores, given what the entity holds now:
 * - a write naming a colour stores that name's register hex as `color`, whatever hex it sent;
 * - in the cloud, a write of a hex only (an older writer) clears a stored name that is not that hex's, so the name never
 *   contradicts the colour; a hex the stored name has keeps it (a blue-or-light-blue hex never loses light-blue);
 * - at a node a hex-only write never stamps `colorName`: until every node declares the field, a node-origin stamp of it
 *   is quarantined by a node on the build before (plan step 0), and its stale name is inert under `catalogColorName`.
 * Fields the write does not name are left out.
 */
export function catalogColorWrite(change: CatalogColorChange, stored: CatalogColorChange | null, atCloud: boolean): CatalogColorChange {
  if (change.colorName !== undefined && change.colorName !== null) return { ...change, color: catalogColorHex(change.colorName) };
  if (change.colorName === null || change.color === undefined || !atCloud) return change;
  const storedName = stored?.colorName;
  if (storedName === undefined || storedName === null) return change;
  return change.color !== null && sameHex(CATALOG_COLOR_HEX[storedName], change.color) ? change : { ...change, colorName: null };
}
